// ============================================================
// THE SUPPORT RING AND THREADS, DRAWN (Squad Mode stage 7; engine/killTeam/
// support): a hairline ring of the support range round the acting
// brother's feet at a third of its strength, and a solid hairline thread
// along the ground to each brother inside it, the metres at its middle -
// all in the team's livery. On the ground, under the figures: never the
// gold marching threads of control or ganging up, which run head to head.
// ============================================================
import { memo } from 'react';
import type { Camera } from '../../engine/battlefield/projection';
import { SCENE_VIEW, project } from '../../engine/battlefield/projection';
import { tokenFoot, type Token } from '../../engine/battlefield/tokens';
import type { SupportView } from '../../engine/killTeam/support';
import type { Livery } from '../../engine/roster/team';
import { liveryStyle } from '../../utils/liveryStyle';
import { groundRing } from './BeatMarks';
import styles from '../../styles/Display.module.css';

export default memo(function SupportThreads({
  view,
  livery,
  tokens,
  camera,
}: {
  view: SupportView | null;
  livery: Livery | null | undefined;
  tokens: Readonly<Record<string, Token>>;
  camera: Camera;
}) {
  const at = view ? tokens[view.actorId] : undefined;
  if (!view || !at) return null;
  const floor = tokenFoot(at);
  const from = project(floor, camera, SCENE_VIEW);
  return (
    <g
      data-testid="kill-team-support"
      // A Solo brother aiming at Enter or Challenge sees what he would have: drawn the same, marked a preview.
      data-preview={view.preview ? 'true' : undefined}
      className={styles.support}
      style={liveryStyle(livery)}
      pointerEvents="none"
    >
      <polygon className={styles.supportRing} points={groundRing(floor, view.rangeM, camera)} />
      {view.threads.map((t) => {
        const to = project(tokenFoot(tokens[t.id]), camera, SCENE_VIEW);
        const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
        return (
          <g key={t.id} data-testid={`kill-team-thread-${t.id}`}>
            <line className={styles.supportThread} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
            <text className={styles.supportPill} x={mid.x} y={mid.y - 4} textAnchor="middle">
              {`${Math.round(t.metres)} m`}
            </text>
          </g>
        );
      })}
    </g>
  );
});
